import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Tabs,
  Switch,
  Popconfirm,
  message,
  Typography,
  Alert,
  Spin,
} from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, RobotOutlined, ThunderboltOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import {
  CatalogItem,
  CatalogFilters,
  CatalogPayload,
  UnclassifiedProduct,
  AuditItem,
  DuplicateCandidate,
  getCatalog,
  getCatalogItem,
  createCatalogItem,
  deleteCatalogItem,
  getUnclassified,
  getAuditItems,
  analyzeProducts,
  getProductTypes,
  getCatalogBrands,
  enrichBatch,
  linkProduct,
  findDuplicates,
} from '../api/productCatalog';
import { ProductType, getProductTypeCatalog } from '../api/productTypeCatalog';
import { oilOrdersApi, OilSupplier } from '../api/oilOrders';
import BrandLogo from '../components/BrandLogo';
import ProductCardDrawer from '../components/product-card/ProductCardDrawer';
import { BASE_TYPES, TypeFieldsFormItems } from '../components/product-card/typeFields';

const { Title } = Typography;

const ProductCatalogPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'catalog';

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<CatalogFilters>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);
  const [unclassified, setUnclassified] = useState<UnclassifiedProduct[]>([]);
  const [unclassifiedLoading, setUnclassifiedLoading] = useState(false);
  const [auditItems, setAuditItems] = useState<AuditItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [suppliers, setSuppliers] = useState<OilSupplier[]>([]);
  const [unclassifiedSupplierId, setUnclassifiedSupplierId] = useState<number | undefined>();

  // AI state
  const [analyzing, setAnalyzing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [allTypes, setAllTypes] = useState<ProductType[]>([]);
  const [createType, setCreateType] = useState<string | undefined>();

  // Классификация неклассифицированных
  const [classifyModalOpen, setClassifyModalOpen] = useState(false);
  const [classifyTarget, setClassifyTarget] = useState<UnclassifiedProduct | null>(null);
  const [classifyType, setClassifyType] = useState<string | undefined>();
  const [duplicates, setDuplicates] = useState<DuplicateCandidate[]>([]);
  const [checkingDupes, setCheckingDupes] = useState(false);
  const [classifyBrand, setClassifyBrand] = useState('');
  const [classifyModelName, setClassifyModelName] = useState('');
  const [classifyForm] = Form.useForm();

  const navigate = useNavigate();
  const [createForm] = Form.useForm();

  useEffect(() => {
    getProductTypes().then(setProductTypes).catch(() => {});
    getCatalogBrands().then(setBrands).catch(() => {});
    getProductTypeCatalog().then(setAllTypes).catch(() => {});
    oilOrdersApi.getSuppliers().then((data) => {
      setSuppliers(data);
      const neva = data.find((item) => item.name === 'НЕВА');
      if (neva) {
        setUnclassifiedSupplierId(neva.id);
      }
    }).catch(() => {});
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCatalog(filters);
      setItems(data);
    } catch {
      message.error('Ошибка загрузки каталога');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadUnclassified = useCallback(async () => {
    setUnclassifiedLoading(true);
    try {
      const data = await getUnclassified(
        unclassifiedSupplierId ? { supplier_id: unclassifiedSupplierId } : undefined,
      );
      setUnclassified(data);
    } catch {
      message.error('Ошибка загрузки неклассифицированных продуктов');
    } finally {
      setUnclassifiedLoading(false);
    }
  }, [unclassifiedSupplierId]);

  const loadAuditItems = useCallback(async () => {
    if (!unclassifiedSupplierId) {
      setAuditItems([]);
      return;
    }
    setAuditLoading(true);
    try {
      const data = await getAuditItems({
        supplier_id: unclassifiedSupplierId,
        only_ai: true,
        only_issues: true,
      });
      setAuditItems(data);
    } catch {
      message.error('Ошибка загрузки аудита AI');
    } finally {
      setAuditLoading(false);
    }
  }, [unclassifiedSupplierId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (activeTab === 'unclassified') {
      loadUnclassified();
    }
    if (activeTab === 'audit') {
      loadAuditItems();
    }
  }, [activeTab, loadUnclassified, loadAuditItems]);

  const handleAnalyze = async () => {
    const fullName = createForm.getFieldValue('full_name_input');
    if (!fullName) return;
    setAnalyzing(true);
    try {
      const [result] = await analyzeProducts([fullName]);
      createForm.setFieldsValue({
        brand: result.brand,
        model_name: result.model_name,
        product_types: result.product_types ?? [],
        viscosity: result.viscosity,
        base_type: result.base_type,
      });
      setCreateType(result.product_types?.[0] ?? undefined);
      if (result.confidence < 0.7) {
        message.warning(`ИИ не уверен (${Math.round(result.confidence * 100)}%). Проверьте поля.`);
      }
    } catch {
      message.error('Ошибка анализа');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnrichBatch = async () => {
    setEnriching(true);
    try {
      const res = await enrichBatch({
        all_unclassified: true,
        supplier_id: unclassifiedSupplierId,
      });
      const supplierName = suppliers.find((item) => item.id === unclassifiedSupplierId)?.name;
      message.success(
        supplierName
          ? `Обогащено (${supplierName}): ${res.enriched} товаров`
          : `Обогащено: ${res.enriched} товаров`,
      );
      loadItems();
      if (activeTab === 'unclassified') {
        loadUnclassified();
      }
      if (activeTab === 'audit') {
        loadAuditItems();
      }
    } catch {
      message.error('Ошибка обогащения');
    } finally {
      setEnriching(false);
    }
  };

  const handleCreate = async (values: CatalogPayload) => {
    try {
      await createCatalogItem(values);
      message.success('Продукт добавлен в каталог');
      setModalOpen(false);
      createForm.resetFields();
      loadItems();
    } catch {
      message.error('Ошибка создания продукта');
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await deleteCatalogItem(selectedItem.id);
      message.success('Продукт удалён');
      setDrawerOpen(false);
      setSelectedItem(null);
      loadItems();
    } catch {
      message.error('Ошибка удаления продукта');
    }
  };

  const openDrawer = (item: CatalogItem) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const openCatalogById = async (catalogId: number) => {
    try {
      const item = await getCatalogItem(catalogId);
      openDrawer(item);
    } catch {
      message.error('Ошибка загрузки карточки каталога');
    }
  };

  // Debounced поиск дублей при изменении brand/model_name в форме классификации
  useEffect(() => {
    if (!classifyBrand || !classifyModelName) {
      setDuplicates([]);
      return;
    }
    setCheckingDupes(true);
    const timer = setTimeout(async () => {
      try {
        const dupes = await findDuplicates(classifyBrand, classifyModelName);
        setDuplicates(dupes);
      } catch {
        setDuplicates([]);
      } finally {
        setCheckingDupes(false);
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      setCheckingDupes(false);
    };
  }, [classifyBrand, classifyModelName]);

  const openClassifyModal = (product: UnclassifiedProduct) => {
    setClassifyTarget(product);
    setClassifyBrand(product.brand);
    setClassifyModelName(product.model_name);
    setClassifyType(undefined);
    setDuplicates([]);
    classifyForm.setFieldsValue({
      brand: product.brand,
      model_name: product.model_name,
      product_types: [],
    });
    setClassifyModalOpen(true);
  };

  const handleLinkToExisting = async (catalogId: number) => {
    if (!classifyTarget) return;
    try {
      await linkProduct(catalogId, classifyTarget.id);
      message.success('Товар привязан к карточке каталога');
      setClassifyModalOpen(false);
      setClassifyTarget(null);
      loadUnclassified();
    } catch {
      message.error('Ошибка привязки товара');
    }
  };

  const handleClassifyCreate = async (values: CatalogPayload) => {
    try {
      const newItem = await createCatalogItem(values);
      await linkProduct(newItem.id, classifyTarget!.id);
      message.success('Карточка создана и товар привязан');
      setClassifyModalOpen(false);
      setClassifyTarget(null);
      loadItems();
      loadUnclassified();
    } catch {
      message.error('Ошибка создания карточки');
    }
  };

  function renderProductTypes(types: string[] | null | undefined) {
    if (!types || types.length === 0) return <span>—</span>;
    return <span style={{ whiteSpace: 'pre-line' }}>{types.join(',\n')}</span>;
  }

  const productTypeOptions = productTypes.map(t => ({ value: t, label: t }));
  const supplierOptions = suppliers.map(item => ({ value: item.id, label: item.name }));

  const columns: ColumnsType<CatalogItem> = [
    {
      title: 'Тип',
      dataIndex: 'product_types',
      key: 'product_types',
      width: 180,
      sorter: (a, b) => (a.product_types?.[0] ?? '').localeCompare(b.product_types?.[0] ?? ''),
      render: (_: any, record: CatalogItem) => renderProductTypes(record.product_types),
    },
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      width: 160,
      sorter: (a, b) => (a.brand ?? '').localeCompare(b.brand ?? ''),
      render: (brand: string, record: CatalogItem) => (
        <Space>
          <BrandLogo brand={brand} />
          {record.ai_enriched && (
            <Tag icon={<RobotOutlined />} color="blue" style={{ fontSize: 11 }}>
              AI {record.ai_confidence ? `${Math.round(record.ai_confidence * 100)}%` : ''}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Наименование',
      dataIndex: 'model_name',
      key: 'model_name',
      ellipsis: true,
    },
    {
      title: 'Статус',
      dataIndex: 'verified',
      key: 'verified',
      width: 120,
      render: (verified: boolean) =>
        verified ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Проверен
          </Tag>
        ) : (
          <Tag color="default">Черновик</Tag>
        ),
    },
  ];

  const unclassifiedColumns: ColumnsType<UnclassifiedProduct> = [
    {
      title: 'Бренд',
      dataIndex: 'brand',
      key: 'brand',
      width: 140,
      sorter: (a, b) => a.brand.localeCompare(b.brand),
      render: (b: string) => <BrandLogo brand={b} />,
    },
    {
      title: 'Наименование',
      dataIndex: 'model_name',
      key: 'model_name',
      ellipsis: true,
    },
    {
      title: 'Norm Key',
      dataIndex: 'norm_key',
      key: 'norm_key',
      ellipsis: true,
    },
    {
      title: 'Тип',
      dataIndex: 'product_types',
      key: 'product_types',
      width: 140,
      render: (_: any, record: UnclassifiedProduct) => renderProductTypes(record.product_types),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: UnclassifiedProduct) => (
        <Button size="small" onClick={() => openClassifyModal(record)}>
          Классифицировать
        </Button>
      ),
    },
  ];

  const issueLabelMap: Record<string, { color: string; label: string }> = {
    unlinked: { color: 'red', label: 'Не привязан' },
    unverified: { color: 'orange', label: 'Не проверен' },
    low_confidence: { color: 'gold', label: 'Низкая уверенность' },
    type_mismatch: { color: 'magenta', label: 'Тип не совпадает' },
    brand_left_in_model: { color: 'volcano', label: 'Бренд в модели' },
    norm_key_changed: { color: 'blue', label: 'Norm key изменён' },
  };

  const auditColumns: ColumnsType<AuditItem> = [
    {
      title: 'Источник',
      key: 'source',
      width: 260,
      render: (_: unknown, record: AuditItem) => (
        <div>
          <div>{record.source_brand}</div>
          <div style={{ color: '#666' }}>{record.source_model_name}</div>
        </div>
      ),
    },
    {
      title: 'Каталог',
      key: 'catalog',
      width: 280,
      render: (_: unknown, record: AuditItem) =>
        record.catalog ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => openCatalogById(record.catalog!.id)}>
            {record.catalog.brand} {record.catalog.model_name}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Типы',
      key: 'types',
      width: 180,
      render: (_: unknown, record: AuditItem) => (
        <div>
          <div>{renderProductTypes(record.source_product_types)}</div>
          <div style={{ color: '#666' }}>{renderProductTypes(record.catalog?.product_types)}</div>
        </div>
      ),
    },
    {
      title: 'AI',
      key: 'ai',
      width: 140,
      render: (_: unknown, record: AuditItem) =>
        record.catalog?.ai_enriched ? (
          <Tag color={record.catalog.ai_confidence != null && record.catalog.ai_confidence < 0.7 ? 'gold' : 'blue'}>
            AI {record.catalog.ai_confidence ? `${Math.round(record.catalog.ai_confidence * 100)}%` : ''}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Проблемы',
      dataIndex: 'issue_flags',
      key: 'issue_flags',
      render: (flags: string[]) => (
        <Space wrap>
          {flags.map((flag) => {
            const meta = issueLabelMap[flag] || { color: 'default', label: flag };
            return <Tag key={flag} color={meta.color}>{meta.label}</Tag>;
          })}
        </Space>
      ),
    },
  ];

  const getTypeFields = (typeName: string | undefined) => {
    if (!typeName) return [];
    return allTypes.find(t => t.name === typeName)?.fields ?? [];
  };

  const catalogFormFields = (
    <>
      <Form.Item label="Полное название (для анализа)" name="full_name_input">
        <Input.Search
          placeholder="Liqui Moly Leichtlauf 5W-40 4л"
          enterButton={
            <Button icon={<RobotOutlined />} loading={analyzing}>
              Анализировать
            </Button>
          }
          onSearch={handleAnalyze}
        />
      </Form.Item>
      <Form.Item name="brand" label="Бренд" rules={[{ required: true, message: 'Укажите бренд' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="model_name" label="Наименование" rules={[{ required: true, message: 'Укажите наименование' }]}>
        <Input />
      </Form.Item>
      <Form.Item name="product_types" label="Тип продукта">
        <Select
          mode="multiple"
          options={productTypeOptions}
          allowClear
          placeholder="Выберите тип(ы)"
          onChange={(val: string[]) => setCreateType(val?.[0])}
        />
      </Form.Item>
      <TypeFieldsFormItems fields={getTypeFields(createType)} />
      {!createType && (
        <>
          <Form.Item name="viscosity" label="Вязкость">
            <Input placeholder="Например: 5W-30" />
          </Form.Item>
          <Form.Item name="base_type" label="Тип базы">
            <Select options={BASE_TYPES} allowClear placeholder="Выберите тип базы" />
          </Form.Item>
          <Form.Item name="specifications" label="Спецификации">
            <Select mode="tags" placeholder="Введите и нажмите Enter" />
          </Form.Item>
        </>
      )}
      <Form.Item name="description" label="Описание">
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item name="verified" label="Проверен" valuePropName="checked">
        <Switch />
      </Form.Item>
    </>
  );

  const filtersRow = (
    <Space wrap style={{ marginBottom: 16 }}>
      <Input.Search
        placeholder="Поиск по названию..."
        allowClear
        style={{ width: 250 }}
        onSearch={(val) => setFilters(prev => ({ ...prev, search: val || undefined }))}
      />
      <Select
        placeholder="Бренд"
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: 200 }}
        options={brands.map(b => ({ value: b, label: b }))}
        onChange={(val) => setFilters(prev => ({ ...prev, brand: val || undefined }))}
      />
      <Select
        placeholder="Тип продукта"
        allowClear
        style={{ width: 200 }}
        options={productTypeOptions}
        onChange={(val) => setFilters(prev => ({ ...prev, product_type: val }))}
      />
      <Select
        placeholder="Тип базы"
        allowClear
        style={{ width: 180 }}
        options={BASE_TYPES}
        onChange={(val) => setFilters(prev => ({ ...prev, base_type: val }))}
      />
      <Space>
        <span>Только проверенные:</span>
        <Switch
          onChange={(checked) => setFilters(prev => ({ ...prev, verified: checked || undefined }))}
        />
      </Space>
    </Space>
  );

  const catalogTab = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Каталог продуктов</Title>
        <Space>
          <Button icon={<SettingOutlined />} onClick={() => navigate('/product-types')}>
            Типы товаров
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            loading={enriching}
            onClick={handleEnrichBatch}
          >
            Обогатить неклассифицированные
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Добавить
          </Button>
        </Space>
      </div>
      {filtersRow}
      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Всего: ${t}` }}
        rowClassName={(record: CatalogItem) =>
          record.ai_enriched && (record.ai_confidence ?? 1) < 0.7 ? 'row-warning' : ''
        }
        onRow={(record) => ({
          onClick: () => openDrawer(record),
          style: { cursor: 'pointer' },
        })}
      />
      <style>{`.row-warning td { background-color: #fffbe6 !important; }`}</style>
    </>
  );

  const unclassifiedTab = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Не классифицированные продукты</Title>
        <Space wrap>
          <Select
            style={{ width: 220 }}
            placeholder="Поставщик"
            allowClear
            options={supplierOptions}
            value={unclassifiedSupplierId}
            onChange={(value) => setUnclassifiedSupplierId(value)}
          />
          <Button
            icon={<ThunderboltOutlined />}
            loading={enriching}
            onClick={handleEnrichBatch}
          >
            Обогатить выборку
          </Button>
        </Space>
      </div>
      <Table
        columns={unclassifiedColumns}
        dataSource={unclassified}
        rowKey="id"
        loading={unclassifiedLoading}
        size="middle"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Всего: ${t}` }}
      />
    </>
  );

  const auditTab = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Проверка AI</Title>
        <Space wrap>
          <Select
            style={{ width: 220 }}
            placeholder="Поставщик"
            allowClear
            options={supplierOptions}
            value={unclassifiedSupplierId}
            onChange={(value) => setUnclassifiedSupplierId(value)}
          />
          <Button onClick={loadAuditItems}>
            Обновить аудит
          </Button>
        </Space>
      </div>
      <Table
        columns={auditColumns}
        dataSource={auditItems}
        rowKey="oil_product_id"
        loading={auditLoading}
        size="middle"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `Всего: ${t}` }}
      />
    </>
  );

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'catalog', label: 'Каталог', children: catalogTab },
          { key: 'unclassified', label: 'Не классифицированные', children: unclassifiedTab },
          { key: 'audit', label: 'Проверка AI', children: auditTab },
        ]}
      />

      {/* Modal: создание нового продукта */}
      <Modal
        title="Новый продукт"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); createForm.resetFields(); setCreateType(undefined); }}
        onOk={() => createForm.submit()}
        okText="Создать"
        cancelText="Отмена"
        width={600}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate} initialValues={{ verified: false }}>
          {catalogFormFields}
        </Form>
      </Modal>

      {/* Modal: классификация неклассифицированного товара */}
      <Modal
        title={classifyTarget ? `Классифицировать: ${classifyTarget.brand} ${classifyTarget.model_name}` : 'Классифицировать'}
        open={classifyModalOpen}
        onCancel={() => { setClassifyModalOpen(false); setClassifyTarget(null); setDuplicates([]); }}
        footer={null}
        width={640}
      >
        <Form
          form={classifyForm}
          layout="vertical"
          onFinish={handleClassifyCreate}
          initialValues={{ verified: false }}
        >
          <Form.Item name="brand" label="Бренд" rules={[{ required: true, message: 'Укажите бренд' }]}>
            <Input onChange={(e) => setClassifyBrand(e.target.value)} />
          </Form.Item>
          <Form.Item name="model_name" label="Наименование" rules={[{ required: true, message: 'Укажите наименование' }]}>
            <Input onChange={(e) => setClassifyModelName(e.target.value)} />
          </Form.Item>

          {/* Результат проверки дублей */}
          {checkingDupes && <Spin size="small" style={{ marginBottom: 12 }} />}
          {!checkingDupes && duplicates.length > 0 && (() => {
            const exactDupe = duplicates.find(d => d.exact);
            if (exactDupe) {
              return (
                <Alert
                  type="error"
                  style={{ marginBottom: 16 }}
                  message="Карточка уже существует в каталоге"
                  description={
                    <Space direction="vertical" size={4}>
                      <span>{exactDupe.brand} — {exactDupe.model_name} ({exactDupe.product_types?.[0] || 'тип не задан'})</span>
                      <Button size="small" type="primary" danger onClick={() => handleLinkToExisting(exactDupe.id)}>
                        Привязать к существующей
                      </Button>
                    </Space>
                  }
                />
              );
            }
            return (
              <Alert
                type="warning"
                style={{ marginBottom: 16 }}
                message="Найдены похожие карточки"
                description={
                  <Space direction="vertical" size={4}>
                    {duplicates.map(d => (
                      <Space key={d.id}>
                        <span>{d.brand} — {d.model_name} ({d.product_types?.[0] || '—'})</span>
                        <Button size="small" onClick={() => handleLinkToExisting(d.id)}>
                          Привязать
                        </Button>
                      </Space>
                    ))}
                  </Space>
                }
              />
            );
          })()}

          <Form.Item name="product_types" label="Тип продукта">
            <Select
              mode="multiple"
              options={productTypeOptions}
              allowClear
              placeholder="Выберите тип(ы)"
              onChange={(val: string[]) => setClassifyType(val?.[0])}
            />
          </Form.Item>
          <TypeFieldsFormItems fields={getTypeFields(classifyType)} />
          {!classifyType && (
            <>
              <Form.Item name="viscosity" label="Вязкость">
                <Input placeholder="Например: 5W-30" />
              </Form.Item>
              <Form.Item name="base_type" label="Тип базы">
                <Select options={BASE_TYPES} allowClear placeholder="Выберите тип базы" />
              </Form.Item>
              <Form.Item name="specifications" label="Спецификации">
                <Select mode="tags" placeholder="Введите и нажмите Enter" />
              </Form.Item>
            </>
          )}
          <Form.Item name="verified" label="Проверен" valuePropName="checked">
            <Switch />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => { setClassifyModalOpen(false); setClassifyTarget(null); setDuplicates([]); }}>
              Отмена
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={duplicates.some(d => d.exact)}
            >
              Создать и привязать
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Drawer: карточка продукта (общий ProductCardDrawer, контекст «catalog») */}
      <ProductCardDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedItem(null); }}
        context="catalog"
        catalogId={selectedItem?.id}
        onItemUpdated={() => loadItems()}
        extra={(item) => item && (
          <Popconfirm
            title="Удалить продукт?"
            description="Это действие необратимо"
            onConfirm={handleDelete}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Button danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        )}
      />
    </>
  );
};

export default ProductCatalogPage;
